import { useIntl } from "react-intl";
import { Divider, MenuItem, Select, SelectChangeEvent } from "@mui/material";

// Compact dropdown of a chart-card header (unit, dimension…), sized like the
// caption title beside it. Options are [value, i18n key]. The card title is
// not linked to the control, hence the explicit accessible name.
const ChartHeaderSelect = <T extends string>({
  value,
  options,
  onChange,
  ariaLabelId,
  plainLabels = false,
  dividerAfter,
}: {
  value: T;
  options: readonly (readonly [T, string])[];
  onChange: (value: T) => void;
  // i18n key of the control's accessible name.
  ariaLabelId: string;
  // Option labels are display text already (names from an API), not i18n keys.
  plainLabels?: boolean;
  // Value after which a rule splits the list (a general option from the rest).
  dividerAfter?: T;
}) => {
  const intl = useIntl();
  const labelOf = (label: string) =>
    plainLabels ? label : intl.formatMessage({ id: label });

  return (
    <Select
      size="small"
      value={value}
      inputProps={{ "aria-label": intl.formatMessage({ id: ariaLabelId }) }}
      onChange={(e: SelectChangeEvent) => onChange(e.target.value as T)}
      sx={{
        fontSize: "0.75rem",
        "& .MuiSelect-select": { py: 0.5 },
        // The outlined border is hidden below, so it can no longer act as the
        // keyboard focus indicator; restore one on the focused element itself.
        "& .MuiSelect-select:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: -2,
          borderRadius: 1,
        },
        "& .MuiSelect-icon": { color: "primary.main" },
        "& .MuiOutlinedInput-notchedOutline": { border: 0 },
        "&:hover .MuiOutlinedInput-notchedOutline": { border: 0 },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 },
      }}
    >
      {options.flatMap(([key, labelId]) => [
        <MenuItem key={key} value={key} sx={{ fontSize: "0.75rem" }}>
          {labelOf(labelId)}
        </MenuItem>,
        ...(key === dividerAfter ? [<Divider key={`${key}-divider`} />] : []),
      ])}
    </Select>
  );
};

export default ChartHeaderSelect;
